import React, { useState, useEffect, useMemo } from 'react';
import api from '../../lib/api';
import ModalShell, { inputClass, selectClass, labelClass, requiredMark, errorBoxClass } from '../ModalShell';

const getErrorMessage = (error) => {
  if (!error.response) return error.message || 'Network error occurred';
  const { status, data } = error.response;
  if (data?.detail) {
    if (Array.isArray(data.detail)) {
      const f = data.detail[0];
      if (typeof f === 'object') return `${f.loc?.[f.loc.length - 1] || 'Field'}: ${f.msg || 'Invalid value'}`;
      return String(f);
    }
    if (typeof data.detail === 'string') return data.detail;
    return JSON.stringify(data.detail);
  }
  return `Request failed with status ${status}`;
};

const formatRs = (n) => `Rs. ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const ageRowClass = (days) => {
  if (days <= 30) return 'bg-[#ECFDF5]';
  if (days <= 60) return 'bg-[#FFFBEB]';
  return 'bg-[#FEF2F2]';
};

const ReceivePaymentModal = ({ customer, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [formData, setFormData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setFetching(true);
        const res = await api.get(`/customers/${customer.id}/outstanding-invoices`);
        if (!cancelled) {
          setInvoices(res.data.invoices || []);
          setTotalOutstanding(res.data.total_outstanding || 0);
        }
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setFetching(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [customer.id]);

  const amountNum = parseFloat(formData.amount) || 0;

  const preview = useMemo(() => {
    let remaining = amountNum;
    return invoices.map((inv) => {
      const bal = Number(inv.balance_due || 0);
      if (remaining <= 0) return { ...inv, applied: 0, state: 'UNCHANGED' };
      if (remaining + 0.001 >= bal) {
        remaining -= bal;
        return { ...inv, applied: bal, state: 'FULLY PAID' };
      }
      const applied = remaining;
      remaining = 0;
      return { ...inv, applied, state: 'PARTIAL', partial_remaining: bal - applied };
    });
  }, [invoices, amountNum]);

  const counts = useMemo(() => {
    let full = 0, partial = 0;
    preview.forEach((p) => { if (p.state === 'FULLY PAID') full++; else if (p.state === 'PARTIAL') partial++; });
    return { full, partial };
  }, [preview]);

  const todayStr = new Date().toISOString().split('T')[0];

  const validate = () => {
    if (!amountNum || amountNum <= 0) return 'Amount must be greater than 0';
    if (amountNum > totalOutstanding + 0.001) return `Amount cannot exceed total outstanding (${formatRs(totalOutstanding)})`;
    if (formData.payment_date > todayStr) return 'Payment date cannot be in the future';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) { setError(v); return; }

    const confirmMsg = `You are recording ${formatRs(amountNum)} received from ${customer.name}. ` +
      `This will pay ${counts.full} invoice${counts.full === 1 ? '' : 's'} fully` +
      (counts.partial > 0 ? ` and partially pay ${counts.partial} invoice${counts.partial === 1 ? '' : 's'}.` : '.');
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      await api.post(`/customers/${customer.id}/receive-payment`, {
        amount: amountNum,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        notes: formData.notes || null,
      });
      onSuccess && onSuccess();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      title={`Receive Payment — ${customer.name}`}
      onClose={onClose}
      maxWidth="max-w-4xl"
      dataTestId="receive-payment-modal"
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm">
            <span className="text-[#6b7280]">Total Outstanding: </span>
            <span className="text-[#EF4444] font-semibold" data-testid="total-outstanding">{formatRs(totalOutstanding)}</span>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#d1d5db] rounded-lg text-[#1a1a2e] hover:bg-white font-medium transition-colors" data-testid="cancel-receive-payment">Cancel</button>
            <button type="submit" form="receive-payment-form" disabled={loading || fetching || invoices.length === 0 || !amountNum} className="px-4 py-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white rounded-lg font-medium transition-colors disabled:opacity-50" data-testid="confirm-receive-payment">{loading ? 'Recording...' : 'Confirm Payment'}</button>
          </div>
        </div>
      }
    >
      <form id="receive-payment-form" onSubmit={handleSubmit}>
        {error && <div className={errorBoxClass} data-testid="receive-payment-error">{error}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Amount Received (Rs.) {requiredMark}</label>
            <input
              type="number" step="0.01" min="0.01" max={totalOutstanding}
              className={`${inputClass} text-xl font-semibold`}
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required data-testid="receive-amount-input" placeholder="0.00"
            />
          </div>
          <div>
            <label className={labelClass}>Payment Method {requiredMark}</label>
            <select className={selectClass} value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} required data-testid="receive-payment-method">
              <option value="cash">Cash</option>
              <option value="bank">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" max={todayStr} className={inputClass} value={formData.payment_date} onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })} data-testid="receive-payment-date" />
          </div>
          <div>
            <label className={labelClass}>Notes</label>
            <input type="text" className={inputClass} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Optional" data-testid="receive-payment-notes" />
          </div>
        </div>

        <h3 className="text-sm font-semibold text-[#1a1a2e] mb-2">Outstanding Invoices (oldest first)</h3>
        {fetching ? (
          <div className="py-8 text-center text-[#6b7280]">Loading invoices...</div>
        ) : invoices.length === 0 ? (
          <div className="py-8 text-center text-[#6b7280]">No outstanding invoices</div>
        ) : (
          <div className="bg-white border border-[#d1d5db] rounded-lg overflow-x-auto">
            <table className="w-full text-sm text-left" data-testid="outstanding-invoices-table">
              <thead className="bg-[#f9fafb] text-[#6b7280] font-medium border-b border-[#e5e7eb]">
                <tr>
                  <th className="py-2 px-3">Invoice No</th>
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3 text-right">Total</th>
                  <th className="py-2 px-3 text-right">Already Paid</th>
                  <th className="py-2 px-3 text-right">Balance Due</th>
                  <th className="py-2 px-3 text-right">Days</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className={`border-b border-[#e5e7eb] ${ageRowClass(inv.days_outstanding)}`}>
                    <td className="py-2 px-3 font-medium">{inv.invoice_number}</td>
                    <td className="py-2 px-3">{inv.invoice_date ? inv.invoice_date.slice(0, 10) : '-'}</td>
                    <td className="py-2 px-3 text-right">{formatRs(inv.total_amount)}</td>
                    <td className="py-2 px-3 text-right">{formatRs(inv.amount_paid)}</td>
                    <td className="py-2 px-3 text-right font-semibold">{formatRs(inv.balance_due)}</td>
                    <td className="py-2 px-3 text-right">{inv.days_outstanding}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {amountNum > 0 && invoices.length > 0 && (
          <div className="mt-4 p-4 bg-white border border-[#d1d5db] rounded-lg" data-testid="fifo-preview">
            <h4 className="text-sm font-semibold text-[#1a1a2e] mb-2">FIFO Allocation Preview</h4>
            <ul className="space-y-1 text-sm">
              {preview.map((p) => (
                <li key={p.invoice_id} data-testid={`preview-${p.invoice_number}`}>
                  <span className="font-medium">{p.invoice_number}</span>{' — '}
                  {p.state === 'FULLY PAID' && <span className="text-[#065F46] font-semibold">FULLY PAID {formatRs(p.applied)}</span>}
                  {p.state === 'PARTIAL' && <span className="text-[#92400E] font-semibold">PARTIAL {formatRs(p.applied)} applied, {formatRs(p.partial_remaining)} remaining</span>}
                  {p.state === 'UNCHANGED' && <span className="text-[#6b7280]">UNCHANGED</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </form>
    </ModalShell>
  );
};

export default ReceivePaymentModal;
